import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import recipes from './soundboardRecipes';
import { findRecipe, BASE_ELEMENTS } from './soundboardUtils';

const SoundBoard = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Initial base elements
  const [elements, setElements] = useState(BASE_ELEMENTS);
  const [craftingArea, setCraftingArea] = useState([]);
  const [discoveries, setDiscoveries] = useState([]);
  const [draggedElement, setDraggedElement] = useState(null);
  const [boardItems, setBoardItems] = useState([]);
  const [scale, setScale] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggedBoardItem, setDraggedBoardItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchDragging, setTouchDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [touchedItem, setTouchedItem] = useState(null);
  const [sidebarTouchedItem, setSidebarTouchedItem] = useState(null);
  const [recipeAnimation, setRecipeAnimation] = useState(null);
  const touchStartRef = useRef(null);
  const lastTapTimeRef = useRef(null);
  const lastClickTimeRef = useRef(null);
  const sidebarScrollingRef = useRef(false);
  const rafIdRef = useRef(null);
  const lastTouchRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });
  const dragTimeoutRef = useRef(null);
  const isDuplicatingRef = useRef(false); // 🔥 NEW: Prevent duplicate copies
  const suppressSidebarClickRef = useRef(false);

  const craftingRef = useRef(null);
  const audioContextRef = useRef(null);
  const canvasRef = useRef(null);
  const boardRef = useRef(null);
  const touchesRef = useRef([]);
  const lastDistanceRef = useRef(0);
  const gestureLastScaleRef = useRef(1);
  const isLoadedRef = useRef(false);

  // 🎯 300+ MEGA RECIPE DATABASE

  // Load game state
  useEffect(() => {
    if (isLoadedRef.current) return;
    isLoadedRef.current = true;

    try {
      const savedState = localStorage.getItem('sportsCraftGameState');
      if (savedState) {
        const parsed = JSON.parse(savedState);

        if (parsed.elements && parsed.elements.length > 0) {
          setElements(parsed.elements);
        }
        if (parsed.boardItems) {
          setBoardItems(parsed.boardItems);
        }
        if (parsed.discoveries) {
          setDiscoveries(parsed.discoveries);
        }
        if (typeof parsed.isDarkMode === 'boolean') {
          setIsDarkMode(parsed.isDarkMode);
        }
        if (typeof parsed.soundEnabled === 'boolean') {
          setSoundEnabled(parsed.soundEnabled);
        }
        if (parsed.scale) {
          setScale(parsed.scale);
        }
        if (parsed.panOffset) {
          setPanOffset(parsed.panOffset);
        }

        console.log('✅ Game state loaded');
      }
    } catch (error) {
      console.error('❌ Failed to load game state:', error);
    }
  }, []);

  // Save game state
  useEffect(() => {
    if (!isLoadedRef.current) return;

    try {
      const gameState = {
        elements,
        boardItems,
        discoveries,
        isDarkMode,
        soundEnabled,
        scale,
        panOffset,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('sportsCraftGameState', JSON.stringify(gameState));
    } catch (error) {
      console.error('❌ Failed to save game state:', error);
    }
  }, [elements, boardItems, discoveries, isDarkMode, soundEnabled, scale, panOffset]);

  const findFreePosition = useCallback((existingItems) => {
    const MIN_DISTANCE = 150;
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    const centerX = (rect.width / 2 - panOffset.x) / scale;
    const centerY = (rect.height / 2 - panOffset.y) / scale;

    if (existingItems.length === 0) {
      return { x: centerX, y: centerY };
    }

    const maxAttempts = 100;
    const searchRadius = 300;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const angle = (attempt / maxAttempts) * Math.PI * 2 * 3;
      const radius = (attempt / maxAttempts) * searchRadius + 50;

      const candidateX = centerX + Math.cos(angle) * radius;
      const candidateY = centerY + Math.sin(angle) * radius;

      let isValid = true;
      for (const item of existingItems) {
        const distance = Math.hypot(candidateX - item.x, candidateY - item.y);
        if (distance < MIN_DISTANCE) {
          isValid = false;
          break;
        }
      }

      if (isValid) {
        return { x: candidateX, y: candidateY };
      }
    }

    const fallbackAngle = Math.random() * Math.PI * 2;
    const fallbackRadius = 200 + Math.random() * 200;
    return {
      x: centerX + Math.cos(fallbackAngle) * fallbackRadius,
      y: centerY + Math.sin(fallbackAngle) * fallbackRadius
    };
  }, [panOffset, scale]);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Particle background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let W = canvas.clientWidth;
    let H = canvas.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const N = isMobile ? 50 : 85;
    const SPEED = 0.25;
    const LINK_DIST = 110;

    const dots = Array.from({ length: N }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * SPEED,
      vy: (Math.random() - 0.5) * SPEED,
      r: 1 + Math.random() * 1.2,
      alpha: 0.35 + Math.random() * 0.5
    }));

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < dots.length; i++) {
        const a = dots[i];
        for (let j = i + 1; j < dots.length; j++) {
          const b = dots[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            ctx.strokeStyle = isDarkMode
              ? `rgba(255,255,255,${0.08 * (1 - d / LINK_DIST)})`
              : `rgba(0,0,0,${0.12 * (1 - d / LINK_DIST)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const p of dots) {
        ctx.fillStyle = isDarkMode
          ? `rgba(255,255,255,${p.alpha * 0.6})`
          : `rgba(0,0,0,${p.alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10) p.y = H + 10;
        if (p.y > H + 10) p.y = -10;
      }

      animationId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    window.addEventListener('resize', handleResize);
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [isDarkMode, isMobile]);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  const playSound = useCallback((type = 'click') => {
    if (!soundEnabled || !audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    let duration = 100;

    switch (type) {
      case 'place':
        oscillator.frequency.setValueAtTime(600, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        duration = 100;
        oscillator.type = 'sine';
        break;

      case 'craft':
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const osc3 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        const gain2 = ctx.createGain();
        const gain3 = ctx.createGain();

        osc1.connect(gain1);
        osc2.connect(gain2);
        osc3.connect(gain3);
        gain1.connect(ctx.destination);
        gain2.connect(ctx.destination);
        gain3.connect(ctx.destination);

        osc1.frequency.setValueAtTime(523.25, ctx.currentTime);
        gain1.gain.setValueAtTime(0.3, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

        osc2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.08);
        gain2.gain.setValueAtTime(0, ctx.currentTime + 0.08);
        gain2.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.09);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.28);

        osc3.frequency.setValueAtTime(783.99, ctx.currentTime + 0.16);
        gain3.gain.setValueAtTime(0, ctx.currentTime + 0.16);
        gain3.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 0.17);
        gain3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

        osc1.type = 'sine';
        osc2.type = 'sine';
        osc3.type = 'sine';

        osc1.start(ctx.currentTime);
        osc2.start(ctx.currentTime + 0.08);
        osc3.start(ctx.currentTime + 0.16);

        osc1.stop(ctx.currentTime + 0.3);
        osc2.stop(ctx.currentTime + 0.38);
        osc3.stop(ctx.currentTime + 0.5);

        return;

      case 'click':
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        duration = 80;
        oscillator.type = 'sine';
        break;

      default:
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        oscillator.type = 'sine';
    }

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  }, [soundEnabled]);

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  const zoomAt = useCallback((clientX, clientY, nextScale) => {
    const el = boardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const localX = clientX - rect.left;
    const localY = clientY - rect.top;

    const worldX = (localX - panOffset.x) / scale;
    const worldY = (localY - panOffset.y) / scale;

    const s = clamp(nextScale, 0.5, 3);
    const newPanX = localX - worldX * s;
    const newPanY = localY - worldY * s;

    setScale(s);
    setPanOffset({ x: newPanX, y: newPanY });
  }, [panOffset, scale]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.002);
        const nextScale = clamp(scale * factor, 0.5, 3);
        zoomAt(e.clientX, e.clientY, nextScale);
        return;
      }

      e.preventDefault();
      let scaleFactor = 1;
      if (e.deltaMode === 1) scaleFactor = 16;
      else if (e.deltaMode === 2) scaleFactor = window.innerHeight;

      setPanOffset(prev => ({
        x: prev.x - e.deltaX * scaleFactor,
        y: prev.y - e.deltaY * scaleFactor
      }));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scale, zoomAt]);

  // 🔥 DESKTOP: RIGHT-CLICK TO PAN
  const handleMouseDown = useCallback((e) => {
    if (e.button === 2 && !draggedBoardItem) { // Right-click
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  }, [panOffset, draggedBoardItem]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
    if (draggedBoardItem) {
      const rect = boardRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - panOffset.x) / scale - dragOffset.x;
      const y = (e.clientY - rect.top - panOffset.y) / scale - dragOffset.y;

      setBoardItems(items =>
        items.map(item =>
          item.id === draggedBoardItem.id ? { ...item, x, y } : item
        )
      );

      const TOUCH_DISTANCE = 80;
      let closestItem = null;
      let closestDistance = TOUCH_DISTANCE;

      for (const otherItem of boardItems) {
        if (otherItem.id !== draggedBoardItem.id) {
          const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestItem = otherItem;
          }
        }
      }

      setTouchedItem(closestItem);
    }
  }, [isPanning, panStart, draggedBoardItem, panOffset, scale, dragOffset, boardItems]);

  const handleMouseUp = useCallback(() => {
    if (draggedBoardItem && touchedItem) {
      const RECIPE_DISTANCE = 80;
      const draggedItem = boardItems.find(item => item.id === draggedBoardItem.id);

      if (draggedItem && touchedItem) {
        const distance = Math.hypot(draggedItem.x - touchedItem.x, draggedItem.y - touchedItem.y);

        if (distance < RECIPE_DISTANCE) {
          const result = findRecipe(draggedItem.name, touchedItem.name, elements);

          if (result && result.name !== `${draggedItem.name}+`) {
            const exists = elements.find(el => el.name === result.name);

            const midX = (draggedItem.x + touchedItem.x) / 2;
            const midY = (draggedItem.y + touchedItem.y) / 2;

            setRecipeAnimation({
              x: midX,
              y: midY,
              emoji: result.emoji,
              name: result.name,
              id: Date.now()
            });

            setTimeout(() => {
              setRecipeAnimation(null);
            }, 700);

            if (!exists) {
              playSound('craft');
              const newElement = {
                id: elements.length + 1,
                name: result.name,
                emoji: result.emoji,
                isBase: false
              };
              setElements(prevEls => [...prevEls, newElement]);
              setDiscoveries(prevD => [...prevD, result.name]);
            } else {
              playSound('place');
            }

            setBoardItems(prev => {
              const filtered = prev.filter(item =>
                item.id !== draggedItem.id && item.id !== touchedItem.id
              );
              return [...filtered, {
                ...result,
                id: Date.now() + Math.random(),
                x: midX,
                y: midY,
                isBase: false
              }];
            });
          }
        }
      }
    }

    setIsPanning(false);
    setDraggedBoardItem(null);
    setTouchedItem(null);
  }, [draggedBoardItem, touchedItem, boardItems, elements, playSound]);

  // 🔥 MOBILE: TWO-FINGER TO PAN
  const handleTouchStart = useCallback((e) => {
    touchesRef.current = Array.from(e.touches);

    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastDistanceRef.current = dist;

      // 🔥 Enable panning with two fingers
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      setPanStart({
        x: midX - panOffset.x,
        y: midY - panOffset.y
      });
      setIsPanning(true);
    }
  }, [panOffset]);

  // 🔥 120FPS SMOOTH DRAG using RAF with optimized state updates
  const updateDragPreview = useCallback((clientX, clientY) => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      // 🔥 OPTIMIZED: Batch state updates
      setDragPreview({
        x: clientX,
        y: clientY
      });

      const rect = boardRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = (clientX - rect.left - panOffset.x) / scale;
      const y = (clientY - rect.top - panOffset.y) / scale;

      const TOUCH_DISTANCE = 80;
      let closestItem = null;
      let closestDistance = TOUCH_DISTANCE;

      for (const otherItem of boardItems) {
        const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = otherItem;
        }
      }

      setSidebarTouchedItem(closestItem);
    });
  }, [boardItems, panOffset, scale]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();

      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);

      const midX = (t0.clientX + t1.clientX) / 2;
      const midY = (t0.clientY + t1.clientY) / 2;

      // 🔥 Two-finger panning with RAF for smoothness
      if (isPanning) {
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
        }
        rafIdRef.current = requestAnimationFrame(() => {
          setPanOffset({
            x: midX - panStart.x,
            y: midY - panStart.y
          });
        });
      }

      // Pinch zoom
      if (lastDistanceRef.current > 0) {
        const deltaScale = dist / lastDistanceRef.current;
        const next = clamp(scale * deltaScale, 0.5, 3);
        zoomAt(midX, midY, next);
      }
      lastDistanceRef.current = dist;
      return;
    }

    if (draggedBoardItem && e.touches.length === 1) {
      e.preventDefault();
      
      // 🔥 OPTIMIZED: Use RAF for smoother board item dragging
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      rafIdRef.current = requestAnimationFrame(() => {
        const rect = boardRef.current.getBoundingClientRect();
        const x = (e.touches[0].clientX - rect.left - panOffset.x) / scale - dragOffset.x;
        const y = (e.touches[0].clientY - rect.top - panOffset.y) / scale - dragOffset.y;

        setBoardItems(items =>
          items.map(item =>
            item.id === draggedBoardItem.id ? { ...item, x, y } : item
          )
        );

        const TOUCH_DISTANCE = 80;
        let closestItem = null;
        let closestDistance = TOUCH_DISTANCE;

        for (const otherItem of boardItems) {
          if (otherItem.id !== draggedBoardItem.id) {
            const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestItem = otherItem;
            }
          }
        }

        setTouchedItem(closestItem);
      });
    }

    // 🔥 ULTRA SMOOTH SIDEBAR DRAG WITH RAF
    if (touchDragging && e.touches.length === 1 && !sidebarScrollingRef.current) {
      e.preventDefault();
      updateDragPreview(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [scale, isPanning, panStart, draggedBoardItem, panOffset, dragOffset, zoomAt, touchDragging, boardItems, updateDragPreview]);

  // 🔥 FIX RECIPE ANIMATION FOR MOBILE SIDEBAR DRAG
  const createRecipeFromSidebarDrag = useCallback((element, touchX, touchY) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (touchX - rect.left - panOffset.x) / scale;
    const y = (touchY - rect.top - panOffset.y) / scale;

    const RECIPE_DISTANCE = 80;
    let closestItem = null;
    let closestDistance = RECIPE_DISTANCE;

    for (const otherItem of boardItems) {
      const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = otherItem;
      }
    }

    if (closestItem) {
      const result = findRecipe(element.name, closestItem.name, elements);

      if (result && result.name !== `${element.name}+`) {
        const exists = elements.find(el => el.name === result.name);

        const midX = (x + closestItem.x) / 2;
        const midY = (y + closestItem.y) / 2;

        // 🔥 SHOW RECIPE ANIMATION
        setRecipeAnimation({
          x: midX,
          y: midY,
          emoji: result.emoji,
          name: result.name,
          id: Date.now()
        });

        setTimeout(() => {
          setRecipeAnimation(null);
        }, 700);

        if (!exists) {
          playSound('craft');
          const newElement = {
            id: elements.length + 1,
            name: result.name,
            emoji: result.emoji,
            isBase: false
          };
          setElements(prevEls => [...prevEls, newElement]);
          setDiscoveries(prevD => [...prevD, result.name]);
        } else {
          playSound('place');
        }

        setBoardItems(prev => {
          const filtered = prev.filter(item => item.id !== closestItem.id);
          return [...filtered, {
            ...result,
            id: Date.now() + Math.random(),
            x: midX,
            y: midY,
            isBase: false
          }];
        });
      } else {
        const newBoardItem = {
          ...element,
          id: Date.now() + Math.random(),
          x,
          y
        };
        setBoardItems(prev => [...prev, newBoardItem]);
        playSound('place');
      }
    } else {
      const newBoardItem = {
        ...element,
        id: Date.now() + Math.random(),
        x,
        y
      };
      setBoardItems(prev => [...prev, newBoardItem]);
      playSound('place');
    }
  }, [boardItems, panOffset, scale, elements, playSound]);

  const handleTouchEnd = useCallback((e) => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (draggedBoardItem && e.touches.length === 0 && touchedItem) {
      const RECIPE_DISTANCE = 80;
      const draggedItem = boardItems.find(item => item.id === draggedBoardItem.id);

      if (draggedItem && touchedItem) {
        const distance = Math.hypot(draggedItem.x - touchedItem.x, draggedItem.y - touchedItem.y);

        if (distance < RECIPE_DISTANCE) {
          const result = findRecipe(draggedItem.name, touchedItem.name, elements);

          if (result && result.name !== `${draggedItem.name}+`) {
            const exists = elements.find(el => el.name === result.name);

            const midX = (draggedItem.x + touchedItem.x) / 2;
            const midY = (draggedItem.y + touchedItem.y) / 2;

            setRecipeAnimation({
              x: midX,
              y: midY,
              emoji: result.emoji,
              name: result.name,
              id: Date.now()
            });

            setTimeout(() => {
              setRecipeAnimation(null);
            }, 700);

            if (!exists) {
              playSound('craft');
              const newElement = {
                id: elements.length + 1,
                name: result.name,
                emoji: result.emoji,
                isBase: false
              };
              setElements(prevEls => [...prevEls, newElement]);
              setDiscoveries(prevD => [...prevD, result.name]);
            } else {
              playSound('place');
            }

            setBoardItems(prev => {
              const filtered = prev.filter(item =>
                item.id !== draggedItem.id && item.id !== touchedItem.id
              );
              return [...filtered, {
                ...result,
                id: Date.now() + Math.random(),
                x: midX,
                y: midY,
                isBase: false
              }];
            });
          }
        }
      }
    }

    if (e.touches.length < 2) {
      lastDistanceRef.current = 0;
    }
    if (e.touches.length === 0) {
      setIsPanning(false);
      setDraggedBoardItem(null);
      setTouchDragging(false);
      setDragPreview(null);
      setTouchedItem(null);
      setSidebarTouchedItem(null);
      sidebarScrollingRef.current = false;
    }
  }, [draggedBoardItem, touchedItem, boardItems, elements, playSound]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;

    const onGestureStart = (e) => {
      e.preventDefault();
      gestureLastScaleRef.current = 1;
    };
    const onGestureChange = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX || (rect.left + rect.width / 2);
      const cy = e.clientY || (rect.top + rect.height / 2);

      const step = e.scale / (gestureLastScaleRef.current || 1);
      gestureLastScaleRef.current = e.scale;
      const next = clamp(scale * step, 0.5, 3);
      zoomAt(cx, cy, next);
    };
    const onGestureEnd = (e) => {
      e.preventDefault();
      gestureLastScaleRef.current = 1;
    };

    el.addEventListener('gesturestart', onGestureStart, { passive: false });
    el.addEventListener('gesturechange', onGestureChange, { passive: false });
    el.addEventListener('gestureend', onGestureEnd, { passive: false });

    return () => {
      el.removeEventListener('gesturestart', onGestureStart);
      el.removeEventListener('gesturechange', onGestureChange);
      el.removeEventListener('gestureend', onGestureEnd);
    };
  }, [scale, zoomAt]);

  // 🔥 FIXED: DOUBLE-CLICK TO DUPLICATE (DESKTOP - ONLY ONE COPY)
  const handleBoardItemClick = useCallback((item) => {
    // 🔥 FIX: Check if we're currently duplicating to prevent multiple copies
    if (isDuplicatingRef.current) return;

    const now = Date.now();
    if (lastClickTimeRef.current && now - lastClickTimeRef.current < 300) {
      // 🔥 FIX: Set flag to prevent concurrent duplications
      isDuplicatingRef.current = true;
      
      // Double-click detected - duplicate item NEAR the original
      const angle = Math.random() * Math.PI * 2;
      const distance = 80 + Math.random() * 40; // Random distance between 80-120px

      const newItem = {
        ...item,
        id: Date.now() + Math.random(),
        x: item.x + Math.cos(angle) * distance,
        y: item.y + Math.sin(angle) * distance
      };
      setBoardItems(prev => [...prev, newItem]);
      playSound('place');
      lastClickTimeRef.current = null;

      // 🔥 FIX: Reset flag after a short delay
      setTimeout(() => {
        isDuplicatingRef.current = false;
      }, 350);
    } else {
      lastClickTimeRef.current = now;
    }
  }, [playSound]);

  const handleBoardItemMouseDown = useCallback((e, item) => {
    e.stopPropagation();
    const rect = boardRef.current.getBoundingClientRect();
    const offsetX = (e.clientX - rect.left - panOffset.x) / scale - item.x;
    const offsetY = (e.clientY - rect.top - panOffset.y) / scale - item.y;
    setDragOffset({ x: offsetX, y: offsetY });
    setDraggedBoardItem(item);
    playSound('click');
  }, [panOffset, scale, playSound]);

  // 🔥 FIXED: DOUBLE-TAP TO DUPLICATE (MOBILE - ONLY ONE COPY)
  const handleBoardItemTouchStart = useCallback((e, item) => {
  e.stopPropagation();
  
  // 🔥 FIX: Check if we're currently duplicating to prevent multiple copies
  if (isDuplicatingRef.current) return;

  if (e.touches.length === 1) {
    const now = Date.now();
    
    // 🔥 FIX: Check for double-tap first
    if (lastTapTimeRef.current && now - lastTapTimeRef.current < 300) {
      // 🔥 FIX: Set flag immediately to prevent concurrent duplications
      isDuplicatingRef.current = true;
      
      // 🔥 Double-tap detected - Clear any pending drag
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
      
      // Duplicate item NEAR the original
      const angle = Math.random() * Math.PI * 2;
      const distance = 80 + Math.random() * 40;

      const newItem = {
        ...item,
        id: Date.now() + Math.random(),
        x: item.x + Math.cos(angle) * distance,
        y: item.y + Math.sin(angle) * distance
      };
      setBoardItems(prev => [...prev, newItem]);
      playSound('place');

      // 🔥 FIX: Clear ref immediately
      lastTapTimeRef.current = null;

      // 🔥 FIX: Reset flag after a short delay
      setTimeout(() => {
        isDuplicatingRef.current = false;
      }, 350);
      
      return; // Exit early to prevent drag from starting
    } else {
      // 🔥 FIXED: Start drag IMMEDIATELY without timeout for smooth movement
      const rect = boardRef.current.getBoundingClientRect();
      const offsetX = (e.touches[0].clientX - rect.left - panOffset.x) / scale - item.x;
      const offsetY = (e.touches[0].clientY - rect.top - panOffset.y) / scale - item.y;
      
      setDragOffset({ x: offsetX, y: offsetY });
      setDraggedBoardItem(item);
      playSound('click');
      
      // Record tap time for double-tap detection
      lastTapTimeRef.current = now;
    }
  }
}, [panOffset, scale, playSound]);


  // 🔥 Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const handleDragStart = (e, element) => {
    setDraggedElement(element);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (!draggedElement) return;

    const rect = boardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / scale;
    const y = (e.clientY - rect.top - panOffset.y) / scale;

    const RECIPE_DISTANCE = 80;
    let closestItem = null;
    let closestDistance = RECIPE_DISTANCE;

    for (const otherItem of boardItems) {
      const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = otherItem;
      }
    }

    if (closestItem) {
      const result = findRecipe(draggedElement.name, closestItem.name, elements);

      if (result && result.name !== `${draggedElement.name}+`) {
        const exists = elements.find(el => el.name === result.name);

        const midX = (x + closestItem.x) / 2;
        const midY = (y + closestItem.y) / 2;

        setRecipeAnimation({
          x: midX,
          y: midY,
          emoji: result.emoji,
          name: result.name,
          id: Date.now()
        });

        setTimeout(() => {
          setRecipeAnimation(null);
        }, 700);

        if (!exists) {
          playSound('craft');
          const newElement = {
            id: elements.length + 1,
            name: result.name,
            emoji: result.emoji,
            isBase: false
          };
          setElements(prevEls => [...prevEls, newElement]);
          setDiscoveries(prevD => [...prevD, result.name]);
        } else {
          playSound('place');
        }

        setBoardItems(prev => {
          const filtered = prev.filter(item => item.id !== closestItem.id);
          return [...filtered, {
            ...result,
            id: Date.now() + Math.random(),
            x: midX,
            y: midY,
            isBase: false
          }];
        });
      } else {
        const newBoardItem = {
          ...draggedElement,
          id: Date.now() + Math.random(),
          x,
          y
        };
        setBoardItems(prev => [...prev, newBoardItem]);
        playSound('place');
      }
    } else {
      const newBoardItem = {
        ...draggedElement,
        id: Date.now() + Math.random(),
        x,
        y
      };
      setBoardItems(prev => [...prev, newBoardItem]);
      playSound('place');
    }

    setDraggedElement(null);
    setSidebarTouchedItem(null);
  }, [draggedElement, panOffset, scale, boardItems, elements, playSound]);

  const handleBoardDrop = useCallback((e) => {
    e.preventDefault();
    if (!draggedElement) return;

    const rect = boardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / scale;
    const y = (e.clientY - rect.top - panOffset.y) / scale;

    const RECIPE_DISTANCE = 80;
    let closestItem = null;
    let closestDistance = RECIPE_DISTANCE;

    for (const otherItem of boardItems) {
      const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = otherItem;
      }
    }

    if (closestItem) {
      const result = findRecipe(draggedElement.name, closestItem.name, elements);

      if (result && result.name !== `${draggedElement.name}+`) {
        const exists = elements.find(el => el.name === result.name);

        const midX = (x + closestItem.x) / 2;
        const midY = (y + closestItem.y) / 2;

        setRecipeAnimation({
          x: midX,
          y: midY,
          emoji: result.emoji,
          name: result.name,
          id: Date.now()
        });

        setTimeout(() => {
          setRecipeAnimation(null);
        }, 700);

        if (!exists) {
          playSound('craft');
          const newElement = {
            id: elements.length + 1,
            name: result.name,
            emoji: result.emoji,
            isBase: false
          };
          setElements(prevEls => [...prevEls, newElement]);
          setDiscoveries(prevD => [...prevD, result.name]);
        } else {
          playSound('place');
        }

        setBoardItems(prev => {
          const filtered = prev.filter(item => item.id !== closestItem.id);
          return [...filtered, {
            ...result,
            id: Date.now() + Math.random(),
            x: midX,
            y: midY,
            isBase: false
          }];
        });
      } else {
        const newBoardItem = {
          ...draggedElement,
          id: Date.now() + Math.random(),
          x,
          y
        };
        setBoardItems(prev => [...prev, newBoardItem]);
        playSound('place');
      }
    } else {
      const newBoardItem = {
        ...draggedElement,
        id: Date.now() + Math.random(),
        x,
        y
      };
      setBoardItems(prev => [...prev, newBoardItem]);
      playSound('place');
    }

    setDraggedElement(null);
    setSidebarTouchedItem(null);
  }, [draggedElement, panOffset, scale, boardItems, elements, playSound]);

  const handleBoardDragOver = (e) => {
    e.preventDefault();

    if (draggedElement) {
      const rect = boardRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - panOffset.x) / scale;
      const y = (e.clientY - rect.top - panOffset.y) / scale;

      const TOUCH_DISTANCE = 80;
      let closestItem = null;
      let closestDistance = TOUCH_DISTANCE;

      for (const otherItem of boardItems) {
        const distance = Math.hypot(x - otherItem.x, y - otherItem.y);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = otherItem;
        }
      }

      setSidebarTouchedItem(closestItem);
    }
  };

  const handleClick = (element) => {
    const position = findFreePosition(boardItems);

    const newBoardItem = {
      ...element,
      id: Date.now() + Math.random(),
      x: position.x,
      y: position.y
    };

    setBoardItems(prev => [...prev, newBoardItem]);
    playSound('place');
  };

  const clearCraft = useCallback(() => {
    if (window.confirm('Clear all progress? This will reset your game!')) {
      setCraftingArea([]);
      setBoardItems([]);
      setElements(BASE_ELEMENTS);
      setDiscoveries([]);
      setScale(1);
      setPanOffset({ x: 0, y: 0 });

      localStorage.removeItem('sportsCraftGameState');
      playSound('click');
    }
  }, [playSound]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
    playSound('click');
  }, [playSound]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
    playSound('click');
  }, [playSound]);

  const filteredElements = elements.filter(el =>
    el.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCurrentLevel = () => {
    const discovered = elements.filter(el => !el.isBase).length;
    if (discovered >= 150) return 10;
    if (discovered >= 120) return 9;
    if (discovered >= 90) return 8;
    if (discovered >= 60) return 7;
    if (discovered >= 40) return 6;
    if (discovered >= 25) return 5;
    if (discovered >= 15) return 4;
    if (discovered >= 8) return 3;
    if (discovered >= 4) return 2;
    return 1;
  };

  const currentLevel = getCurrentLevel();

  const bgColor = isDarkMode ? '#0b0c0e' : '#ffffff';
  const textColor = isDarkMode ? '#e5e7eb' : '#111111';
  const panelBg = isDarkMode ? '#0e1012' : '#ffffff';
  const panelBorder = isDarkMode ? '#1f2428' : '#ececec';
  const chipBg = isDarkMode ? '#0f1316' : '#f6f6f6';
  const ghostColor = isDarkMode ? '#262b31' : '#e5e7eb';

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      background: bgColor,
      color: textColor,
      fontFamily: '"Hind Siliguri", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      transition: 'all 0.3s ease'
    }}>
      <canvas
        ref={canvasRef}
        style={{ position: 'fixed', inset: 0, zIndex: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      />

      <div style={{
        position: 'fixed', left: '16px', top: '12px', zIndex: 10,
        fontWeight: 700, fontSize: isMobile ? '18px' : '28px',
        letterSpacing: '0.5px', userSelect: 'none', fontStyle: 'italic'
      }}>
        INFINITY CRAFT
      </div>

      {!isMobile && (
        <div style={{
          position: 'fixed', right: '400px', top: '14px', zIndex: 10,
          textAlign: 'right', lineHeight: 1.05, userSelect: 'none'
        }}>
          <div style={{ fontSize: '30px', fontWeight: 700, opacity: 0.95 }}>Level {currentLevel}</div>
        </div>
      )}

      {isMobile && (
        <div style={{
          position: 'fixed', right: '16px', top: '12px', zIndex: 10,
          textAlign: 'right', lineHeight: 1.05, userSelect: 'none'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 700, opacity: 0.95 }}>Level {currentLevel}</div>
        </div>
      )}

      {dragPreview && draggedElement && (
        <div
          style={{
            position: 'fixed',
            left: dragPreview.x - 50,
            top: dragPreview.y - 30,
            zIndex: 9999,
            pointerEvents: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            borderRadius: '14px',
            background: chipBg,
            border: `2px solid ${isDarkMode ? '#4a90e2' : '#2563eb'}`,
            boxShadow: '0 8px 24px rgba(0,0,0,.4)',
            opacity: 0.95,
            transform: 'scale(1.05)',
            fontWeight: 600,
            willChange: 'transform'
          }}
        >
          <span style={{ fontSize: isMobile ? '22px' : '22px' }}>{draggedElement.emoji}</span>
<span style={{ fontSize: isMobile ? '14px' : '14px' }}>{draggedElement.name}</span>
  </div>
      )}

      <div
        ref={boardRef}
        style={{
          position: 'fixed',
          inset: 0,
          right: isMobile ? 0 : '360px',
          bottom: isMobile ? '176px' : 0,
          overflow: 'hidden',
          cursor: isPanning ? 'grabbing' : 'default',
          touchAction: 'none',
          zIndex: 1
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDrop={handleBoardDrop}
        onDragOver={handleBoardDragOver}
      >
        <div
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            width: '100%',
            height: '100%',
            position: 'relative',
            willChange: draggedBoardItem ? 'transform' : 'auto'
          }}
        >
          {boardItems.map((item) => {
            const isTouched = touchedItem?.id === item.id || sidebarTouchedItem?.id === item.id;
            const isDragged = draggedBoardItem?.id === item.id;

            return (
              <div
                key={item.id}
                onMouseDown={(e) => handleBoardItemMouseDown(e, item)}
                onClick={() => handleBoardItemClick(item)}
                onTouchStart={(e) => handleBoardItemTouchStart(e, item)}
                style={{
                  position: 'absolute',
                  left: `${item.x}px`,
                  top: `${item.y}px`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '6px',
                  padding: isMobile ? '6px 8px' : '8px 10px',
                  borderRadius: '12px',
                  background: chipBg,
                  border: isTouched && !isDragged
                    ? `2px solid ${isDarkMode ? '#10b981' : '#059669'}`
                    : `1px solid ${ghostColor}`,
                  cursor: 'grab',
                  userSelect: 'none',
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '13px',
                  boxShadow: isTouched && !isDragged
                    ? `0 0 12px ${isDarkMode ? 'rgba(16, 185, 129, 0.4)' : 'rgba(5, 150, 105, 0.4)'}`
                    : '0 2px 4px rgba(0,0,0,.1), 0 6px 12px rgba(0,0,0,.08)',
                  transition: isDragged ? 'none' : 'all 0.15s ease',
                  zIndex: isDragged ? 1000 : (isTouched ? 999 : 1),
                  transform: isMobile ? 'translate(-50%, -50%)' : 'none',
                  willChange: isDragged ? 'transform' : 'auto'
                }}
              >
<span style={{ fontSize: isMobile ? '16px' : '18px' }}>{item.emoji}</span>
<span style={{ fontSize: isMobile ? '14px' : '13px' }}>{item.name}</span>


              </div>
            );
          })}

          {recipeAnimation && (
            <div
              style={{
                position: 'absolute',
                left: `${recipeAnimation.x}px`,
                top: `${recipeAnimation.y}px`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 9999,
                animation: 'simpleMatchSuccess 0.7s ease-out forwards',
                willChange: 'transform, opacity'
              }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  fontSize: '48px',
                  animation: 'simpleScale 0.35s ease-out'
                }}>
                  {recipeAnimation.emoji}
                </div>
                <div style={{
                  background: isDarkMode ? '#1e293b' : '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  border: `2px solid ${isDarkMode ? '#10b981' : '#059669'}`
                }}>
                  {recipeAnimation.name}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <div style={{
          position: 'fixed', right: 0, top: 0, width: '360px', height: '100%',
          background: panelBg, borderLeft: `1px solid ${panelBorder}`, zIndex: 3,
          display: 'flex', flexDirection: 'column', transition: 'all 0.3s ease'
        }}>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {elements.filter(el => el.isBase).map(element => (
                <div
                  key={element.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, element)}
                  onClick={() => handleClick(element)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 14px', borderRadius: '14px', background: chipBg,
                    border: `1px solid ${ghostColor}`, cursor: 'grab', userSelect: 'none',
                    fontWeight: 600, boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.05)',
                    transition: 'all 0.2s'
                  }}
                >
<span style={{ fontSize: '18px' }}>{element.emoji}</span>
<span style={{ fontSize: '12px', lineHeight: 1.2 }}>{element.name}</span>

                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '0 16px 16px 16px' }}>
            <div style={{ color: '#7a7a7a', fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
              Discoveries ({filteredElements.filter(el => !el.isBase).length}/{recipes.length})
            </div>
            <div>
              {filteredElements.filter(el => !el.isBase).map(element => (
                <div
                  key={element.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, element)}
                  onClick={() => handleClick(element)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', borderRadius: '12px', background: chipBg,
                    border: `1px solid ${ghostColor}`, fontWeight: 600, margin: '6px 6px 0 0',
                    cursor: 'grab', fontSize: '14px'
                  }}
                >
                  <span>{element.emoji}</span>
                  <span>{element.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 4,
            background: panelBg,
            borderTop: `1px solid ${panelBorder}`,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            height: '176px',
            maxHeight: '176px'
          }}
        >
          <div
            style={{
              padding: '8px 10px',
              borderBottom: `1px solid ${panelBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: panelBg
            }}
          >
            <div
              onClick={() => alert(`Level ${currentLevel}\nDiscovered: ${elements.filter(el => !el.isBase).length}/${recipes.length}`)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: `1px solid ${ghostColor}`,
                background: chipBg,
                padding: '6px 10px',
                borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 600,
                fontSize: '12px'
              }}
            >
              <span>🏆</span>
              <span>Lv.{currentLevel} • {elements.filter(el => !el.isBase).length}</span>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <div
                onClick={toggleDarkMode}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9px',
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${ghostColor}`,
                  background: chipBg,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                  fontSize: '16px'
                }}
              >
                {isDarkMode ? '☀️' : '🌙'}
              </div>
              <div
                onClick={clearCraft}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9px',
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${ghostColor}`,
                  background: chipBg,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                  fontSize: '16px'
                }}
              >
                🧹
              </div>
              <div
                onClick={toggleSound}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9px',
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${ghostColor}`,
                  background: chipBg,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                  fontSize: '16px'
                }}
              >
                {soundEnabled ? '🔊' : '🔇'}
              </div>
            </div>
          </div>

         <div
 style={{
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '8px 8px 9px',
    flex: 1,
    width: '100%',
    minWidth: 0,
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-x',
    overscrollBehaviorX: 'contain',
    scrollBehavior: 'smooth'
  }}
>
  <div
    style={{
      display: 'grid',
      gridTemplateRows: 'repeat(2, 42px)',
      gridAutoFlow: 'column',
      gridAutoColumns: 'max-content',
      gap: '7px',
      alignContent: 'start',
      minWidth: 'max-content'
    }}
  >

              {elements.map(element => {
                const resetSidebarTouchState = () => {
                  touchStartRef.current = null;
                  sidebarScrollingRef.current = false;
                  setDraggedElement(null);
                  setTouchDragging(false);
                  setDragPreview(null);
                  setSidebarTouchedItem(null);
                };

                const handleTouchStartElement = (e) => {
                  suppressSidebarClickRef.current = false;
                  touchStartRef.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                    time: Date.now(),
                    element: element,
                    moved: false,
                    intent: null,
                    scrolling: false,
                    scrollY: e.currentTarget.closest('[style*="overflow"]')?.scrollTop || 0
                  };
                  sidebarScrollingRef.current = false;
                };

                const handleTouchMoveElement = (e) => {
                  if (!touchStartRef.current) return;

                  const moveX = e.touches[0].clientX - touchStartRef.current.x;
                  const moveY = e.touches[0].clientY - touchStartRef.current.y;
                  const deltaX = Math.abs(moveX);
                  const deltaY = Math.abs(moveY);

                  if (touchStartRef.current.intent === 'scroll') {
                    sidebarScrollingRef.current = true;
                    return;
                  }

                  if (!touchStartRef.current.moved) {
                    if (deltaX > 8 && deltaX > deltaY + 6) {
                      suppressSidebarClickRef.current = true;
                      touchStartRef.current.intent = 'scroll';
                      touchStartRef.current.scrolling = true;
                      sidebarScrollingRef.current = true;
                      setDraggedElement(null);
                      setTouchDragging(false);
                      setSidebarTouchedItem(null);
                      return;
                    }

                    if (moveY < -12 && deltaY > deltaX + 4) {
                      suppressSidebarClickRef.current = true;
                      touchStartRef.current.intent = 'drag';
                      touchStartRef.current.moved = true;
                      sidebarScrollingRef.current = false;
                      e.preventDefault();
                      setDraggedElement(element);
                      setTouchDragging(true);
                      updateDragPreview(e.touches[0].clientX, e.touches[0].clientY);
                    }

                    return;
                  }

                  if (touchDragging && touchStartRef.current.intent === 'drag' && !sidebarScrollingRef.current) {
                    e.preventDefault();
                    updateDragPreview(e.touches[0].clientX, e.touches[0].clientY);
                  }
                };

                const handleTouchEndElement = (e) => {
                  if (!touchStartRef.current) return;

                  const wasMoved = touchStartRef.current.moved;
                  const wasScrolling = sidebarScrollingRef.current || touchStartRef.current.intent === 'scroll';

                  if (wasMoved || wasScrolling) {
                    suppressSidebarClickRef.current = true;
                  }

                  if (touchDragging && draggedElement && draggedElement.id === element.id && !wasScrolling && wasMoved) {
                    const touch = e.changedTouches[0];
                    const boardRect = boardRef.current?.getBoundingClientRect();

                    if (boardRect && touch.clientY < boardRect.bottom) {
                      createRecipeFromSidebarDrag(element, touch.clientX, touch.clientY);
                    }
                  }

                  resetSidebarTouchState();
                };

                return (
                  <div
                    key={element.id}
                    onTouchStart={handleTouchStartElement}
                    onTouchMove={handleTouchMoveElement}
                    onTouchEnd={handleTouchEndElement}
                    onTouchCancel={resetSidebarTouchState}
                    onClick={() => {
                      if (suppressSidebarClickRef.current) {
                        suppressSidebarClickRef.current = false;
                        return;
                      }
                      handleClick(element);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Add ${element.name} to the craft board`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick(element);
                      }
                    }}
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      borderRadius: '9px',
                      background: touchDragging && draggedElement?.id === element.id ? ghostColor : chipBg,
                      border: `1px solid ${ghostColor}`,
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontWeight: 600,
                      boxShadow: touchDragging && draggedElement?.id === element.id
                        ? '0 4px 8px rgba(0,0,0,.2)'
                        : '0 1px 2px rgba(0,0,0,.06), 0 2px 8px rgba(0,0,0,.04)',
                      transition: 'all 0.15s',
                      height: '42px',
                      maxWidth: '156px',
                      textAlign: 'center',
                      opacity: touchDragging && draggedElement?.id === element.id ? 0.4 : 1,
                      touchAction: 'pan-x',
                      willChange: touchDragging && draggedElement?.id === element.id ? 'transform, opacity' : 'auto'
                    }}
                  >
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>{element.emoji}</span>
                    <span style={{
                      fontSize: '11px',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>{element.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!isMobile && (
        <>
          <div style={{ position: 'fixed', left: '12px', bottom: '12px', zIndex: 5 }}>
            <div
              onClick={() => alert(`Level ${currentLevel}\nDiscovered: ${elements.filter(el => !el.isBase).length}/${recipes.length}\n\n💾 Auto-saved!`)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: `1px solid ${ghostColor}`,
                background: panelBg,
                padding: '8px 10px',
                borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.05)',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              <span>🏆</span>
              <span>Lv.{currentLevel} • {elements.filter(el => !el.isBase).length}/{recipes.length}</span>
            </div>
          </div>

          <div style={{ position: 'fixed', right: '380px', bottom: '10px', zIndex: 5, display: 'flex', gap: '8px' }}>
            <div
              onClick={toggleDarkMode}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                display: 'grid',
                placeItems: 'center',
                border: `1px solid ${ghostColor}`,
                background: panelBg,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.05)',
                fontSize: '16px'
              }}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </div>
            <div
              onClick={clearCraft}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                display: 'grid',
                placeItems: 'center',
                border: `1px solid ${ghostColor}`,
                background: panelBg,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.05)',
                fontSize: '16px'
              }}
            >
              🧹
            </div>
            <div
              onClick={toggleSound}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                display: 'grid',
                placeItems: 'center',
                border: `1px solid ${ghostColor}`,
                background: panelBg,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.05)',
                fontSize: '16px'
              }}
            >
              {soundEnabled ? '🔊' : '🔇'}
            </div>
          </div>
        </>
      )}

      {!isMobile && boardItems.length === 0 && (
        <div style={{
          position: 'fixed',
          inset: 0,
          right: '360px',
          display: 'grid',
          placeItems: 'center',
          pointerEvents: 'none',
          color: isDarkMode ? '#94a3b8' : '#64748b',
          fontWeight: 600,
          fontSize: '18px',
          zIndex: 1
        }}>
          🎯 Right-
          🎯 Right-click to pan • Scroll to zoom • Drag elements to combine
        </div>
      )}

      {isMobile && boardItems.length === 0 && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          color: ghostColor,
          fontWeight: 600,
          fontSize: '14px',
          zIndex: 1,
          padding: '0 20px'
        }}>
          🎯 Two fingers to pan/zoom<br/>Tap elements to add<br/>Drag to combine
        </div>
      )}

      <style>{`
        @keyframes simpleMatchSuccess {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0;
          }
          15% {
            transform: translate(-50%, -50%) scale(1.15);
            opacity: 1;
          }
          30% {
            transform: translate(-50%, -50%) scale(0.95);
            opacity: 1;
          }
          40% {
            transform: translate(-50%, -50%) scale(1.05);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0;
          }
        }

        @keyframes simpleScale {
          0% {
            transform: scale(0.5);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }

        * {
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
        }

        body {
          overscroll-behavior: none;
        }

        @media (max-width: 767px) {
          body {
            overflow: hidden !important;
            position: fixed !important;
            width: 100% !important;
            height: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SoundBoard;
